import React, { useEffect, useRef } from "react";
import { Form, Input, Button, Row, Col, DatePicker, Select } from "antd";
import styled from "@emotion/styled";
import moment from "moment";
import {
  NON_EMPTY_REGEX,
  NON_CHARACTER,
  VALIDATE_CHARACTER,
  TIMEZONELIST,
  countryList,
} from "common/Constants";
import { connect } from "react-redux";
import { getHolidayList } from "redux/selectors";
import { useTranslation } from "react-i18next";

const StyledButton = styled(Button)`
  background: #26a4ff;
  border-radius: 5px;
  color: #ffffff;
  width: 100px;
  margin-top: 20px;
  font-weight: bold;
`;

const Styleddiv = styled.div`
  .ant-form-item-label > label {
    font-weight: bold;
  }
`;

const BasicInformation = ({
  step,
  setStep,
  title,
  setValues,
  values,
  alldatas,
  holidayList,
}) => {
  const [form] = Form.useForm();
  const containerRef = useRef(null);
  const { t } = useTranslation();

  const validateDateRange = (date) => {
    const endDate = moment(form.getFieldValue("end_date"));
    if (endDate < date) {
      form.setFieldsValue({ end_date: date });
    }
  };

  const onFinish = (values) => {
    setValues(values);
    setStep(step + 1);
  };

  useEffect(() => {
    form.setFieldsValue(values);
  }, [values]);

  return (
    <Styleddiv>
      <Form
        form={form}
        name="AddDepartment"
        layout="vertical"
        initialValues={{
          name: "",
          latitude: "",
          longtitude: "",
          start_date: "",
          end_date: "",
          usertimezone: "",
          country: "",
          description: "",
        }}
        onFinish={onFinish}
      >
        {/* Country */}
        {title === "Add Location" && (
          <Form.Item
            name="country"
            label={t("Country")}
            rules={[{ required: true, message: t("Please select country") }]}
          >
            <Select
              showSearch
              placeholder={t("Search to Select")}
              optionFilterProp="children"
            >
              {countryList?.map((country, index) => (
                <Select.Option key={index} value={country} label={country}>
                  {country}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {/* Name */}
        <Form.Item
          label={
            title === "Add Location"
              ? t("Location Name")
              : title === "holiday"
              ? t("Holiday Name")
              : title === "break"
              ? t("Break Name")
              : title === "levels"
              ? t("Level Name")
              : t("Department Name")
          }
          name="name"
          rules={[
            {
              required: true,
              message:
                title === "Add Location"
                  ? t("Please enter location name")
                  : title === "holiday"
                  ? t("Please enter holiday name")
                  : title === "break"
                  ? t("Please enter break name")
                  : title === "levels"
                  ? t("Please enter the level name")
                  : t("Please enter department name"),
            },
            {
              validator: (rule, value) => {
                if (
                  value &&
                  !VALIDATE_CHARACTER.test(value) &&
                  title !== "holiday" &&
                  title !== "department"
                ) {
                  return Promise.reject(
                    title === "Add Location"
                      ? t("Location Name should not contain special characters")
                      : title === "break"
                      ? t("Break Name should not contain special characters")
                      : title === "levels"
                      ? t("Level Name should not contain special characters")
                      : t(
                          "Department Name should not contain special characters"
                        )
                  );
                }
                return Promise.resolve();
              },
            },
            {
              validator: (rule, value) => {
                if (value) {
                  const trimmedValue = value.trim();
                  if (title === "holiday") {
                    if (
                      alldatas.some(
                        (data) => data.holiday_name === trimmedValue
                      )
                    ) {
                      return Promise.reject(t("Holiday already exists"));
                    }
                  } else if (title === "break") {
                    if (
                      alldatas.some(
                        (data) => data.breakName.trim() === trimmedValue
                      )
                    ) {
                      return Promise.reject(t("Break already exists"));
                    }
                  } else if (title === "Add Location") {
                    if (
                      alldatas.some(
                        (data) => data.location_name === trimmedValue
                      )
                    ) {
                      return Promise.reject(t("Location already exists"));
                    }
                  } else {
                    if (
                      alldatas.some(
                        (data) => data.department_name === trimmedValue
                      )
                    ) {
                      return Promise.reject(t("Department already exists"));
                    }
                  }
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            placeholder={
              title === "Add Location"
                ? t("Location Name")
                : title === "holiday"
                ? t("Holiday Name")
                : title === "break"
                ? t("Break Name")
                : title === "levels"
                ? t("Level Name")
                : t("Department Name")
            }
            maxLength={25}
          />
        </Form.Item>

        {/* Timezone */}
        {title === "Add Location" && (
          <>
            <Form.Item
              name="usertimezone"
              label={t("Time Zone")}
              rules={[
                { required: true, message: t("Please select Time zone") },
              ]}
            >
              <Select showSearch placeholder={t("Search to Select")}>
                {TIMEZONELIST?.map((item, index) => (
                  <Select.Option key={index} value={item} label={item}>
                    {item}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={t("Latitude")}
              name="location_latitude"
              rules={[
                {
                  required: true,
                  pattern: NON_CHARACTER,
                  message: t("Please enter latitude!"),
                },
              ]}
            >
              <Input placeholder={t("Enter latitude")} maxLength={20} />
            </Form.Item>

            <Form.Item
              label={t("Longitude")}
              name="location_longitude"
              rules={[
                {
                  required: true,
                  pattern: NON_CHARACTER,
                  message: t("Please enter longitude!"),
                },
              ]}
            >
              <Input placeholder={t("Enter longitude")} maxLength={20} />
            </Form.Item>
          </>
        )}

        {/* Holiday Dates */}
        {title === "holiday" && (
          <Row>
            <Col xs={24} ref={containerRef}>
              <Form.Item
                label={t("Start date")}
                name="start_date"
                rules={[
                  {
                    required: true,
                    pattern: NON_EMPTY_REGEX,
                    message: t("Please enter start date!"),
                  },
                  () => ({
                    validator(rule, value) {
                      const date = moment(value);

                      if (title === "holiday" && holidayList?.length > 0) {
                        const isHolidayOverlap = holidayList.some((item) => {
                          const itemStartDate = moment(item.start_date);
                          const itemEndDate = moment(item.end_date);
                          return (
                            date.isBetween(
                              itemStartDate,
                              itemEndDate,
                              undefined,
                              "[]"
                            ) ||
                            date.isSame(itemStartDate, "day") ||
                            date.isSame(itemEndDate, "day")
                          );
                        });

                        if (isHolidayOverlap) {
                          return Promise.reject(
                            t("Holiday Date already exist")
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  className="w-100"
                  format="DD-MM-YYYY"
                  onChange={validateDateRange}
                  placeholder={t("Select start date")}
                  getPopupContainer={() => containerRef.current}
                />
              </Form.Item>
            </Col>

            <Col xs={24} ref={containerRef}>
              <Form.Item
                label={t("End date")}
                name="end_date"
                rules={[
                  {
                    required: true,
                    pattern: NON_EMPTY_REGEX,
                    message: t("Please enter end date!"),
                  },
                  ({ getFieldValue }) => ({
                    validator(rule, value) {
                      const date = moment(value);
                      const startDate = moment(getFieldValue("start_date"));

                      if (date.isBefore(startDate, "day")) {
                        return Promise.reject(
                          t("End date cannot be before start date")
                        );
                      }

                      if (title === "holiday" && holidayList?.length > 0) {
                        const isHolidayOverlap = holidayList.some((item) => {
                          const itemStartDate = moment(item.start_date);
                          const itemEndDate = moment(item.end_date);
                          return (
                            date.isBetween(
                              itemStartDate,
                              itemEndDate,
                              undefined,
                              "[]"
                            ) ||
                            date.isSame(itemStartDate, "day") ||
                            date.isSame(itemEndDate, "day")
                          );
                        });

                        if (isHolidayOverlap) {
                          return Promise.reject(
                            t("Holiday Date already exist")
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  className="w-100"
                  format="DD-MM-YYYY"
                  placeholder={t("Select end date")}
                  getPopupContainer={() => containerRef.current}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* Description */}
        {title === "levels" && (
          <Form.Item
            label={t("Description")}
            name="description"
            rules={[
              {
                required: true,
                message: t("Please enter Description"),
              },
            ]}
          >
            <Input placeholder={t("Enter Description")} maxLength={100} />
          </Form.Item>
        )}

        <div className="text-end w-100">
          <StyledButton type="primary" htmlType="submit">
            {t("Next")}
          </StyledButton>
        </div>
      </Form>
    </Styleddiv>
  );
};

const mapStateToProps = (state) => ({
  holidayList: getHolidayList(state),
});

export default connect(mapStateToProps, null)(BasicInformation);
